import {
  parseFlexibleTimestamp,
  scoreTimestampHeader,
  headerWords,
  KW_TIER1_PATTERNS,
  KW_TIER2_PATTERNS,
  KW_EXCLUSION_PATTERNS,
  isKWhHeader,
  parseStrictNumber,
} from '../fileParser';
import type { ColumnProfile, ColumnStats, HeaderSignal, ColumnKind } from './types';

const FORMULA_RE = /[\d.\-]+\s+X\s+[\d.]+\s*=\s*[\d.\-]+/;
const UNIT_STRING_RE = /^(kw|kwh|kva|kvar|kw-h|wh)$/i;
/** A clock reading inside a cell: "19:15", "7:15 PM", "00:00:00". */
const CLOCK_TIME_RE = /\b\d{1,2}:\d{2}\b/;

/** Distinct values are kept only to read low-cardinality columns (units, IDs);
 *  a demand column has thousands and none of them are wanted. */
const DISTINCT_VALUE_CAP = 10;

function computeMedian(values: number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  return sorted[Math.floor(sorted.length / 2)];
}

function buildHeaderSignal(header: string | null): HeaderSignal {
  if (!header) {
    return { timestampScore: 0, demandTier: 0, excluded: false, isKwh: false, isKva: false, isGeneration: false, isUnitColumn: false };
  }
  const h = header.toLowerCase().trim();
  const hNorm = h.replace(/[^a-z0-9]/g, '');
  // Snake_case and CamelCase spellings must signal exactly what the spaced
  // spelling signals: "voltage_avg" is a voltage column and "net_kw" is demand.
  // Every pattern is tried against the words as well as the raw header, so a
  // header that matched before still matches; this only adds the readings that
  // an underscore used to hide from `\b`.
  const hWords = headerWords(header);

  const timestampScore = scoreTimestampHeader(header);

  let demandTier: 0 | 1 | 2 = 0;
  if (KW_TIER1_PATTERNS.some(p => p.test(h) || p.test(hNorm) || p.test(hWords))) demandTier = 1;
  else if (KW_TIER2_PATTERNS.some(p => p.test(h) || p.test(hNorm) || p.test(hWords))) demandTier = 2;

  const excluded = KW_EXCLUSION_PATTERNS.some(p => p.test(h) || p.test(hWords));
  const isKwh = isKWhHeader(header);
  const isKva = /kva|kvar/i.test(h);
  const isGeneration = /gen|solar|pv|produc|export|backfeed|reverse|flow.{0,5}f/i.test(h);
  const isUnitColumn = /unit|measure/i.test(h);

  return { timestampScore, demandTier, excluded, isKwh, isKva, isGeneration, isUnitColumn };
}

export function profileColumns(
  rows: unknown[][],
  headerRow: (string | null)[] | null
): ColumnProfile[] {
  if (rows.length === 0) return [];

  const colCount = Math.max(...rows.map(r => (r as unknown[]).length), 0);
  if (colCount === 0) return [];

  const sample = rows.slice(0, 500);
  const profiles: ColumnProfile[] = [];

  for (let c = 0; c < colCount; c++) {
    const header = headerRow ? (headerRow[c] ?? null) : null;
    const headerSignal = buildHeaderSignal(header);

    const cells = sample.map(r => (r as unknown[])[c] ?? null);

    const numericValues: number[] = [];
    const dateValues: Date[] = [];
    const distinctSet = new Set<string>();
    const sampleValues: unknown[] = [];
    let nonNullCount = 0;
    let formulaTextCount = 0;
    let unitStringCount = 0;
    // Cells the spreadsheet itself typed as dates (xlsx/xls/ods). A typed date
    // is the file's own statement that the column holds dates, and is counted
    // separately from strings that merely parse as one.
    let typedDateCount = 0;
    // String cells carrying a clock reading. Interval timestamps virtually
    // always do; a date-only column, an id, or a label never does.
    let clockTimeCount = 0;

    for (const cell of cells) {
      if (cell === null || cell === undefined || cell === '') continue;
      nonNullCount++;

      const str = String(cell);
      distinctSet.add(str);
      if (sampleValues.length < 5) sampleValues.push(cell);

      if (FORMULA_RE.test(str)) formulaTextCount++;
      if (UNIT_STRING_RE.test(str.trim())) unitStringCount++;

      const num = parseStrictNumber(cell);
      if (num !== null) numericValues.push(num);

      if (cell instanceof Date && !isNaN(cell.getTime())) {
        typedDateCount++;
        dateValues.push(cell);
      } else if (num === null || (num > 40000 && num < 60000)) {
        // A bare number is not a date. `new Date("2")` is a valid instant in
        // JavaScript, so without this a column of small readings that happened
        // to rise for a few rows counted as an ordered date column. The one
        // numeric shape that is a date — an Excel serial written out as text —
        // stays eligible by its range, as parseFlexibleTimestamp already limits it.
        const d = parseFlexibleTimestamp(str);
        if (d) dateValues.push(d);
        if (CLOCK_TIME_RE.test(str)) clockTimeCount++;
      }
    }

    const numericCount = numericValues.length;
    const parseableDateCount = dateValues.length;
    const distinctCount = distinctSet.size;

    // Monotonic in either direction. National Grid exports newest-first, and
    // scoring only ascending runs left its date columns looking unordered, which
    // dropped them out of the datetime test and into the numeric candidates.
    let monotonicRatio: number | undefined;
    if (parseableDateCount > 1) {
      let increasing = 0;
      let decreasing = 0;
      for (let i = 1; i < dateValues.length; i++) {
        const delta = dateValues[i].getTime() - dateValues[i - 1].getTime();
        if (delta > 0) increasing++;
        else if (delta < 0) decreasing++;
      }
      monotonicRatio = Math.max(increasing, decreasing) / (dateValues.length - 1);
    }

    let min: number | undefined;
    let max: number | undefined;
    let median: number | undefined;
    if (numericValues.length > 0) {
      min = Math.min(...numericValues);
      max = Math.max(...numericValues);
      median = computeMedian(numericValues);
    }

    const isAllZero = numericCount > 5 && numericValues.every(v => v === 0);
    const isConstant = distinctCount === 1 && nonNullCount > 5;

    const stats: ColumnStats = {
      nonNullCount, numericCount, parseableDateCount, distinctCount, typedDateCount,
      min, max, median, monotonicRatio, isAllZero, isConstant,
    };

    // ── Kind classification (first match wins) ──────────────────────────────
    let kind: ColumnKind = 'unknown';
    let confidence = 0.5;

    if (nonNullCount > 0 && formulaTextCount / nonNullCount >= 0.3) {
      kind = 'formula-text';
      confidence = 0.8;
    } else if (nonNullCount === 0) {
      kind = 'empty';
      confidence = 1.0;
    } else {
      const dateRatio = parseableDateCount / nonNullCount;
      const numRatio = numericCount / nonNullCount;
      const mono = monotonicRatio ?? 0;
      const typedRatio = typedDateCount / nonNullCount;
      const clockRatio = clockTimeCount / nonNullCount;
      const distinctRatio = distinctCount / nonNullCount;

      // Four independent ways a column proves it holds timestamps. Any one is
      // enough. The first two were the only two until 2026-09-08, when a
      // Burlington Electric export sorted by demand (so not in time order) with
      // a snake_case header (so no header score) satisfied neither and was
      // refused, although every cell in the column was a typed Excel date.
      //
      //   orderedDates  the values run in time order, as most exports do
      //   namedDates    the header says it is a timestamp
      //   typedDates    the spreadsheet itself typed the cells as dates
      //   clockedDates  non-numeric strings, nearly all distinct, nearly all
      //                 carrying a clock reading, nearly all parsing as dates —
      //                 the shape of an interval timestamp column and of
      //                 nothing else that appears in a meter export
      //
      // Row order is never required. A utility can hand us its readings in any
      // order it likes; the extractor sorts them.
      const orderedDates = dateRatio >= 0.7 && mono >= 0.7;
      const namedDates = dateRatio >= 0.5 && headerSignal.timestampScore > 0;
      const typedDates = typedRatio >= 0.7;
      const clockedDates = dateRatio >= 0.9 && numRatio <= 0.1 && clockRatio >= 0.9 && distinctRatio >= 0.9;

      if (orderedDates || namedDates || typedDates || clockedDates) {
        kind = 'datetime';
        // Typed cells and clocked strings stand in for the order evidence a
        // shuffled file cannot supply; a column that has both order and type
        // scores no higher than it did before.
        const orderScore = Math.max(mono, typedDates ? 1 : 0, clockedDates ? 0.8 : 0);
        confidence = Math.min(1, 0.5 * dateRatio + 0.3 * orderScore + 0.2 * Math.min(headerSignal.timestampScore / 100, 1));
      } else if (nonNullCount > 0 && unitStringCount / nonNullCount >= 0.1) {
        kind = 'unit-string';
        confidence = 0.8;
      } else if (distinctCount <= 3 && nonNullCount > 5 && numRatio < 0.5) {
        kind = 'id-like';
        confidence = 0.6;
      } else if (numRatio >= 0.7) {
        const med = median ?? 0;
        if (med < 0.05) {
          kind = 'numeric-tiny';
          confidence = 0.5;
        } else if (med > 500) {
          kind = 'numeric-large';
          confidence = 0.6;
        } else if (med >= 0.05 && med <= 200 && headerSignal.isKwh) {
          kind = 'numeric-energy';
          confidence = Math.min(1, 0.5 * numRatio + 0.3 + (headerSignal.demandTier > 0 ? 0.2 : 0));
        } else if (med >= 0.05 && med <= 200 && !headerSignal.excluded) {
          kind = 'numeric-demand';
          const tierScore = headerSignal.demandTier === 1 ? 1 : headerSignal.demandTier === 2 ? 0.6 : 0.2;
          confidence = Math.min(1, 0.4 * numRatio + 0.4 * tierScore + 0.2 * (headerSignal.excluded ? 0 : 1));
        } else {
          kind = 'numeric-other';
          confidence = 0.4;
        }
      }
    }

    confidence = Math.min(1, Math.max(0, confidence));

    // Distinct values are attached for unit columns only. They travel into the
    // audit payload with the rest of the per-column stats, and a low-cardinality
    // text column can hold a customer or rate-class name; a unit column holds
    // 'kW' and 'kWh'.
    if (kind === 'unit-string' && distinctCount <= DISTINCT_VALUE_CAP) {
      stats.distinctValues = [...distinctSet];
    }

    profiles.push({ index: c, header, kind, confidence, samples: sampleValues, stats, headerSignal });
  }

  return profiles;
}
